import { ShieldCheck } from "@phosphor-icons/react";
import { type ComponentPropsWithoutRef, forwardRef } from "react";
import { useTranslation } from "react-i18next";

import { type AskPunttSource } from "./types";

export type InlineSourceCitationDensity = "cell" | "block";

export type InlineSourceCitationProps = Omit<
  ComponentPropsWithoutRef<"button">,
  "children"
> & {
  source: AskPunttSource;
  testIdPrefix: string;
  density?: InlineSourceCitationDensity;
  /** Extra sources beyond the primary title (renders as +N). */
  extraCount?: number;
  /** Visible text in place of the title, e.g. a citation number. Pair it with `ariaLabel`. */
  label?: string;
  /** Accessible name in place of the title's; must contain `label` (WCAG 2.5.3). */
  ariaLabel?: string;
  /** Replaces the generated testid when another surface already names this chip. */
  testId?: string;
  /**
   * The accent badge, and `aria-pressed`. Omit it for a chip that is not a toggle.
   */
  selected?: boolean;
};

/** The corner of a citation badge. The saved-memory chip sits beside these badges and takes the same one. */
export const SOURCE_BADGE_RADIUS_CLASS = "rounded-[3px]";

/**
 * The badge's look, shared by every surface that shows a citation number: the
 * in-text badge, the chips under an answer, and the rail's Cited claims cards
 * read as one numbered set. `selected` is the card or chip the reader focused.
 */
export function sourceBadgeClass(selected = false): string {
  return [
    // why: the memory chip is a 1px outline, and a borderless number sat flatter beside it.
    `inline-flex h-5 items-center justify-center border ${SOURCE_BADGE_RADIUS_CLASS} px-2 text-[12px] font-medium leading-4 tracking-[0.04px]`,
    selected
      ? "border-puntt-accent-9 bg-puntt-accent-9 text-base-white"
      : "border-puntt-neutral-gray-6 bg-puntt-neutral-gray-3 text-puntt-neutral-gray-11",
  ].join(" ");
}

/**
 * The rail card around a citation: claim cards, a cited chat, and a memory
 * share it, so each reads as the same kind of row.
 */
export function citationCardClass(selected = false): string {
  return [
    "flex w-full flex-col gap-1 rounded-lg border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9",
    selected
      ? "border-puntt-accent-9 bg-puntt-accent-2"
      : "border-puntt-neutral-gray-5 bg-base-white hover:border-puntt-neutral-gray-7 hover:bg-puntt-neutral-gray-1",
  ].join(" ");
}

// why forwardRef + rest props: SourceHoverCard renders this inside
// HoverCard.Trigger asChild, and the Slot hands its ref and pointer handlers to
// this component. Dropping them leaves the card unable to open on hover.
export const InlineSourceCitation = forwardRef<
  HTMLButtonElement,
  InlineSourceCitationProps
>(function InlineSourceCitation(
  {
    source,
    testIdPrefix,
    density = "block",
    extraCount = 0,
    label: labelOverride,
    ariaLabel: ariaLabelOverride,
    testId,
    selected,
    className,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation("ui");
  const showShield = density === "block" && source.kind === "knowledge";
  const showExtra = extraCount > 0;
  const titleLabel = showExtra
    ? t("askPuntt.sources.inlineLabelWithExtra", {
        title: source.title,
        count: extraCount,
      })
    : source.title;
  const label = labelOverride ?? titleLabel;

  const ariaLabel =
    ariaLabelOverride ??
    (showExtra
      ? t("askPuntt.sources.inlineAriaWithExtra", {
          title: source.title,
          count: extraCount,
        })
      : t("askPuntt.sources.inlineAria", { title: source.title }));

  return (
    <button
      {...rest}
      ref={ref}
      type="button"
      data-testid={testId ?? `${testIdPrefix}-inline-source-${source.id}`}
      aria-label={ariaLabel}
      {...(selected === undefined
        ? {}
        : {
            "aria-pressed": selected,
            "data-selected": selected ? "true" : "false",
          })}
      className={[
        sourceBadgeClass(selected ?? false),
        "cursor-pointer gap-1 overflow-hidden text-left",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {showShield ? (
        <ShieldCheck
          size={12}
          aria-hidden="true"
          data-testid={`${testIdPrefix}-inline-source-shield-${source.id}`}
          className="shrink-0"
        />
      ) : null}
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
});
